import { clsx } from "clsx";
import { type FC, type ReactElement } from "react";
import Select from "react-select";
import { type BaseFieldArrayProps, FieldArray, type Validator, type WrappedFieldArrayProps } from "redux-form";
import { TriggerButton } from "widgets";

import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import { FormNote } from "@/ui-modules/core/form/layout/FormNote.js";
import CheckboxField, { CheckboxReduxFormField } from "@/ui-modules/core/redux-form/CheckboxField.js";

import { type ParentType, type ParentTypeSet } from "../../../graphql.js";
import { TooltipController } from "../overlays/components/OverlayControllers.js";
import InputField, { InputReduxFormField } from "./InputField.js";

export interface IParentTypeFieldProps extends Partial<WrappedFieldArrayProps> {
  //fields?: any; // injected as WrappedFieldArrayProps
  //meta?: any; // injected as WrappedFieldArrayProps

  title?: string | ReactElement;
  options?: any[];
  sampleTypeHasData?: boolean;
  isLoading?: boolean;
  validate?: Validator | Validator[];
  handlePrimaryChanged?: (index: number) => void;
  handleRecyclableChanged?: (index: number) => void;
  openConfigureDialog?: (parentTypeId: number) => void;
  onOpenList?: () => void;
  parentTypeAdded?: (parentType: ParentType) => void; // callback when a parent type is added
  parentTypeRemoved?: (parentType: ParentType) => void; // callback when a parent type is removed
}

export const IS_PRIMARY_FIELD = "isPrimary";
export const IS_RECYCLABLE_FIELD = "isRecyclable";
export const HIDDEN_TOUCH = "hiddenTouch"; // to make sure the sync validation is triggered when we change this one, after we changed the defaults (o be revmoed when redux-form v8 is ready, with method for forcing a syncValidation)

/**
 * Parent Type Field field to be used in context of redux-form
 *
 */
const ParentTypeField: FC<IParentTypeFieldProps> = (props) => {
  // create a list of all IDs of the currently selected parent types
  const selectedParentTypeIds: number[] = props.fields.map((_element, index) => {
    return props.fields.get(index).parentType.id;
  });

  // do not show the currently selected options in the dropdown
  const parentTypeOptions: ParentType[] = props.options
    ? props.options.filter(
        (parentType: ParentType) =>
          !selectedParentTypeIds.find((id: number) => {
            return id === parentType.id;
          }),
      )
    : [];

  const addParentType = (parentType: ParentType) => {
    // set the first added parent type to primary
    props.fields.push({ isPrimary: props.fields.length === 0, parentType });
    props.parentTypeAdded(parentType);
  };

  const removeParentType = (parentType: ParentType) => {
    props.parentTypeRemoved(parentType);
  };

  const selectClassNames: string = clsx("react-select");

  const changeForbiddenTooltip: string =
    props.sampleTypeHasData &&
    "The parent types can only be modified if there are no samples associated with the sample type";
  const selector: ReactElement = (
    <TooltipController content={changeForbiddenTooltip || undefined} overlayOptions={{ delay: 600 }}>
      {(tooltipTriggerProps) => (
        <span
          onPointerEnter={tooltipTriggerProps.onPointerEnter}
          onPointerLeave={tooltipTriggerProps.onPointerLeave}
          ref={tooltipTriggerProps.ref}
        >
          <Select
            className={selectClassNames}
            classNamePrefix={"react-select"}
            onMenuOpen={props.onOpenList}
            name="parentTypeSelect"
            placeholder={"Select parent type..."}
            isClearable={false}
            isSearchable={true}
            getOptionValue={(option: ParentType) => option.id}
            getOptionLabel={(option: ParentType) => option.name}
            options={parentTypeOptions}
            onChange={addParentType}
            noOptionsMessage={() => "No matching parent type..."}
            isDisabled={!!changeForbiddenTooltip}
            isLoading={props.isLoading}
            controlShouldRenderValue={false}
            menuPosition={"absolute"}
            menuPortalTarget={document.body}
            menuShouldScrollIntoView={false}
            styles={{ menuPortal: (provided) => ({ ...provided, zIndex: 9999 }) }}
          />
        </span>
      )}
    </TooltipController>
  );

  const selectorWidget: ReactElement = (
    <div className="parent-type-selector noselect">
      {props.fields.map((element: string, index) => {
        const removeParentTypeByIndex = () => {
          const set: ParentTypeSet = props.fields.get(index);
          props.fields.remove(index);
          removeParentType(set.parentType);
        };

        // CAUTION: the "map" call returns a string, since "map" is not our standard "map" but provided by redux-form
        // Therefore we need to retrieve the parent type explicitly via redux-form "get":
        const parentTypeSet: ParentTypeSet = props.fields.get(index);
        const parentType: ParentType = parentTypeSet.parentType;
        const label: string = parentType.name;

        const recyclableDisabled = !parentTypeSet[IS_PRIMARY_FIELD];
        const configureLinkDisabled = !parentTypeSet.parentType;

        const primaryTooltip =
          "When new samples are registered, users are allowed to enter attribute values for the primary parent. Every sample type must have one primary parent type.";
        const recyclableTooltip: string = recyclableDisabled
          ? "This option is only available for primary parent types"
          : "When enabled, new samples can be added to an existing parent entity. When disabled, a new parent is created for every new set of samples.";

        return (
          <div key={`selected-parent-type-${index}`}>
            <div className="parent-type-selector-selection">
              <TooltipController content={label}>
                {(tooltipTriggerProps) => (
                  <div
                    onPointerEnter={tooltipTriggerProps.onPointerEnter}
                    onPointerLeave={tooltipTriggerProps.onPointerLeave}
                    ref={tooltipTriggerProps.ref}
                    className="parent-type-selector-item-label"
                  >
                    {label}
                  </div>
                )}
              </TooltipController>

              <div className="parent-type-selector-item-configure">
                <TriggerButton
                  icon={"edit"}
                  label={"Configure Attributes"}
                  onPress={() => props.openConfigureDialog(parentType.id)}
                  isDisabled={configureLinkDisabled ? "This parent type is not configurable" : false}
                />
              </div>

              <div className="parent-type-selector-item-checkbox">
                <CheckboxReduxFormField
                  name={`${element}.${IS_PRIMARY_FIELD}`}
                  label={"Primary"}
                  component={CheckboxField}
                  disabled={!!changeForbiddenTooltip}
                  onChange={() => props.handlePrimaryChanged(index)}
                  tooltipText={changeForbiddenTooltip || primaryTooltip}
                  style={{ paddingBottom: 0 }}
                />
              </div>
              <div className="parent-type-selector-item-checkbox">
                <CheckboxReduxFormField
                  name={`${element}.${IS_RECYCLABLE_FIELD}`}
                  label={"Reusable"}
                  component={CheckboxField}
                  disabled={!!changeForbiddenTooltip || recyclableDisabled}
                  onChange={() => props.handleRecyclableChanged(index)}
                  tooltipText={changeForbiddenTooltip || recyclableTooltip}
                  style={{ paddingBottom: 0 }}
                />
              </div>
              <div className="parent-type-selector-item-checkbox" style={{ display: "none" }}>
                <InputReduxFormField name={`${element}.${HIDDEN_TOUCH}`} component={InputField} />
              </div>
              <div>
                <TriggerButton
                  icon={"delete"}
                  label={"Remove"}
                  isDisabled={changeForbiddenTooltip}
                  onPress={removeParentTypeByIndex}
                />
              </div>
            </div>
          </div>
        );
      })}
      {selector}
    </div>
  );

  return (
    <FormInputGroup>
      {props.title && <FormLabel>{props.title}</FormLabel>}
      {selectorWidget}
      {props.meta.invalid && <FormError>{props.meta.error}</FormError>}
      {props.fields.length > 1 ? (
        <FormNote>
          When selecting more than one parent type, make sure to choose the correct primary parent type.
        </FormNote>
      ) : null}
    </FormInputGroup>
  );
};

export default ParentTypeField;

export function ParentTypeReduxFormField(props: IParentTypeFieldProps & BaseFieldArrayProps) {
  return <FieldArray {...props} />;
}
